import { useState } from "react";
import { addCurrency } from "../../services";
import { ScaleLoader } from "react-spinners";

interface AddCurrencyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function AddCurrencyModal({
    isOpen,
    onClose,
    onSuccess,
}: AddCurrencyModalProps) {
    const [currency, setCurrency] = useState("");
    const [rate, setRate] = useState<number | "">("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!currency || !rate) return;

        try {
            setLoading(true);
            setError(null);

            await addCurrency({
                currency: currency.toUpperCase(),
                rate: Number(rate),
            });

            if (onSuccess) onSuccess();
            setCurrency("");
            setRate("");
            onClose();
        } catch (err: any) {
            console.error("Error adding currency:", err);
            setError("Failed to add currency. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                    Add Currency
                </h2>

                {error && (
                    <p className="mb-3 text-sm text-red-500">
                        {error}
                    </p>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Currency Code
                        </label>
                        <input
                            type="text"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                            placeholder="e.g. USD"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-sm text-white shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500/50"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Rate
                        </label>
                        <input
                            type="number"
                            value={rate}
                            onChange={(e) => setRate(Number(e.target.value))}
                            placeholder="e.g. 1751"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-sm text-white shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500/50"
                            required
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                            Cancel
                        </button>

                        {loading ? (
                            <ScaleLoader color="#ffffff" />
                        ) : (
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                Save
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
